(function (global) {
  'use strict';

  const VERSION = '0.2.0-six-packages';

  const PACKAGE_DEFINITIONS = Object.freeze({
    'all-season-stretch': {
      code: 'all-season-stretch',
      title: 'All-Season Stretch Package',
      climate: 'All-Season',
      shirtFamily: 'stretch',
      targetPrice: 175000,
      suitCount: 2,
      slacks: 4,
      shirts: 10,
      ties: 6,
      socks: 10,
      belts: 2,
      sewingKits: 1,
      careBooklets: 1,
      enabled: true,
    },
    'all-season-traditional': {
      code: 'all-season-traditional', title: 'All-Season Traditional Package', climate: 'All-Season',
      shirtFamily: 'traditional', targetPrice: 150000, suitCount: 2, slacks: 4, shirts: 10,
      ties: 6, socks: 10, belts: 2, sewingKits: 1, careBooklets: 1, enabled: true,
    },
    'warm-weather-stretch': {
      code: 'warm-weather-stretch', title: 'Warm Weather Stretch Package', climate: 'Warm Weather',
      shirtFamily: 'stretch', targetPrice: 149000, suitCount: 1, slacks: 5, shirts: 10,
      ties: 6, socks: 10, belts: 2, sewingKits: 1, careBooklets: 1, enabled: true,
    },
    'warm-weather-traditional': {
      code: 'warm-weather-traditional', title: 'Warm Weather Traditional Package', climate: 'Warm Weather',
      shirtFamily: 'traditional', targetPrice: 124000, suitCount: 1, slacks: 5, shirts: 10,
      ties: 6, socks: 10, belts: 2, sewingKits: 1, careBooklets: 1, enabled: true,
    },
    'hot-weather-stretch': {
      code: 'hot-weather-stretch', title: 'Hot Weather Stretch Package', climate: 'Hot Weather',
      shirtFamily: 'stretch', targetPrice: 123000, suitCount: 0, slacks: 6, shirts: 10,
      ties: 6, socks: 10, belts: 2, sewingKits: 1, careBooklets: 1, enabled: true,
    },
    'hot-weather-traditional': {
      code: 'hot-weather-traditional', title: 'Hot Weather Traditional Package', climate: 'Hot Weather',
      shirtFamily: 'traditional', targetPrice: 98000, suitCount: 0, slacks: 6, shirts: 10,
      ties: 6, socks: 10, belts: 2, sewingKits: 1, careBooklets: 1, enabled: true,
    },
  });

  function normalize(value) {
    return String(value ?? '')
      .toLowerCase()
      .replace(/&/g, ' and ')
      .replace(/[^a-z0-9]+/g, ' ')
      .trim();
  }

  function compact(value) {
    return normalize(value).replace(/\s+/g, '');
  }

  function canonicalSize(value) {
    const raw = String(value ?? '').trim();
    const n = normalize(raw);
    if (!n) return '';

    const numberMatch = n.match(/^(\d+(?:\.\d+)?)/);
    if (numberMatch && /collar|neck/.test(n)) return numberMatch[1];

    if (/\b(x small|xsmall|xs)\b/.test(n)) return 'XS';
    if (/\b(small|sm)\b/.test(n) && !/x small|xsmall/.test(n)) return 'S';
    if (/\b(medium|md|med)\b/.test(n)) return 'M';
    if (/\b(large|lg)\b/.test(n) && !/x large|xlarge|extra large/.test(n)) return 'L';
    if (/\b(x large|xlarge|extra large|xl)\b/.test(n) && !/2x|xxl|3x|xxxl|4x|xxxxl|5x/.test(n)) return 'XL';
    if (/\b(2x large|2xlarge|2xl|xxl)\b/.test(n)) return '2XL';
    if (/\b(3x large|3xlarge|3xl|xxxl)\b/.test(n)) return '3XL';
    if (/\b(4x large|4xlarge|4xl|xxxxl)\b/.test(n)) return '4XL';
    if (/\b(5x large|5xlarge|5xl)\b/.test(n)) return '5XL';

    return raw.toUpperCase();
  }

  function variantOption(variant, index) {
    if (!variant || index == null || index < 0) return '';
    if (Array.isArray(variant.options) && variant.options[index] != null) {
      return String(variant.options[index]);
    }
    return String(variant[`option${index + 1}`] ?? '');
  }

  function optionNames(product) {
    return (product?.options || []).map((option) =>
      typeof option === 'string' ? option : option?.name || ''
    );
  }

  function findOptionIndex(product, patterns, exclude = []) {
    const names = optionNames(product);
    const regexes = patterns.map((pattern) => pattern instanceof RegExp ? pattern : new RegExp(pattern, 'i'));
    return names.findIndex((name, index) => !exclude.includes(index) && regexes.some((regex) => regex.test(name)));
  }

  function inventoryStatus(variant, quantity = 1) {
    const requested = Math.max(0, Number(quantity) || 0);
    const policy = variant?.inventoryPolicy || null;
    const canSpecialOrder = policy === 'continue';
    const tracked = Boolean(variant?.inventoryManagement) && Number.isFinite(Number(variant?.inventoryQuantity));

    if (!variant) {
      return { requested, tracked: false, canSpecialOrder: false, availableNow: 0, toOrder: 0, allowed: false };
    }

    if (!tracked) {
      const allowed = Boolean(variant.available || canSpecialOrder);
      return {
        requested,
        tracked: false,
        canSpecialOrder,
        availableNow: allowed ? requested : 0,
        toOrder: 0,
        allowed,
      };
    }

    const onHand = Math.max(0, Number(variant.inventoryQuantity) || 0);
    const availableNow = Math.min(requested, onHand);
    const toOrder = Math.max(0, requested - availableNow);
    return {
      requested,
      tracked: true,
      canSpecialOrder,
      onHand,
      availableNow,
      toOrder,
      allowed: (variant.available || canSpecialOrder) && (toOrder === 0 || canSpecialOrder),
    };
  }

  function canFulfillQuantity(variant, quantity = 1) {
    return inventoryStatus(variant, quantity).allowed;
  }

  function normalizedVariant(product, variant) {
    return {
      ...variant,
      id: String(variant.id),
      productId: String(product.id ?? ''),
      productHandle: product.handle || '',
      productTitle: product.title || '',
      price: Number(variant.price) || 0,
      available: Boolean(variant.available),
      inventoryQuantity: variant.inventoryQuantity == null || variant.inventoryQuantity === ''
        ? null
        : Number(variant.inventoryQuantity),
      inventoryPolicy: variant.inventoryPolicy || null,
      inventoryManagement: variant.inventoryManagement || null,
      image: variant.image || product.featuredImage || '',
      sku: variant.sku || '',
      options: Array.isArray(variant.options) ? variant.options.map(String) : [],
    };
  }

  function flattenProducts(products) {
    const rows = [];
    (products || []).forEach((product) => {
      (product.variants || []).forEach((variant) => rows.push({ product, variant: normalizedVariant(product, variant) }));
    });
    return rows;
  }

  function sumAllocation(allocation) {
    return Object.values(allocation || {}).reduce((sum, value) => sum + (Number(value) || 0), 0);
  }

  function updateAllocation(allocation, variantId, delta, max = Infinity) {
    const next = { ...(allocation || {}) };
    const current = Number(next[variantId]) || 0;
    const value = Math.max(0, Math.min(max, current + Number(delta || 0)));
    if (value > 0) next[variantId] = value;
    else delete next[variantId];
    return next;
  }

  function uniqueSorted(values, sorter) {
    const unique = Array.from(new Set(values.filter((value) => value != null && value !== '')));
    return unique.sort(sorter || ((a, b) => String(a).localeCompare(String(b), undefined, { numeric: true })));
  }

  function colorKey(label, matchMode) {
    const value = String(label || '').trim();
    if (matchMode === 'exact') return normalize(value);
    const leadingCode = value.match(/^([A-Za-z0-9]+)(?:\s*[-–—]\s*|\s+|$)/);
    return leadingCode ? leadingCode[1].toLowerCase() : normalize(value);
  }

  function indexSuitProduct(product, component, matchMode) {
    if (!product) return null;
    const names = optionNames(product);
    let colorIndex = names.findIndex((name) => /colou?r/i.test(name));
    if (colorIndex < 0) colorIndex = 0;
    let sizeIndex = names.findIndex((name, index) => index !== colorIndex && /(size|waist|coat|chest|length)/i.test(name));
    if (sizeIndex < 0) sizeIndex = names.findIndex((_, index) => index !== colorIndex);

    const colors = new Map();
    const variants = (product.variants || []).map((variant, order) => {
      const row = normalizedVariant(product, variant);
      row._pmsColorLabel = variantOption(row, colorIndex);
      row._pmsColorKey = colorKey(row._pmsColorLabel, matchMode);
      row._pmsSize = sizeIndex >= 0 ? variantOption(row, sizeIndex) : row.title;
      row._pmsOrder = order;
      if (!colors.has(row._pmsColorKey)) {
        colors.set(row._pmsColorKey, { key: row._pmsColorKey, label: row._pmsColorLabel, variants: [], order });
      }
      colors.get(row._pmsColorKey).variants.push(row);
      return row;
    });
    return { ...product, variants, _pms: { component, names, colorIndex, sizeIndex, colors } };
  }

  function createSuitProgram(program) {
    const matchMode = program.matchMode || 'leading-code';
    const coat = indexSuitProduct(program.coat, 'coat', matchMode);
    const pant = indexSuitProduct(program.pant, 'pant', matchMode);
    if (!coat || !pant) return null;
    return {
      id: String(program.id || coat.id || coat.handle),
      title: program.title || coat.title,
      matchMode,
      coat,
      pant,
      image: coat.featuredImage || '',
    };
  }

  function suitColorChoices(program) {
    const choices = [];
    program.coat._pms.colors.forEach((coatColor, key) => {
      const pantColor = program.pant._pms.colors.get(key);
      if (!pantColor) return;
      const available = coatColor.variants.some((variant) => canFulfillQuantity(variant, 1)) &&
        pantColor.variants.some((variant) => canFulfillQuantity(variant, 2));
      choices.push({ key, label: coatColor.label, available, order: coatColor.order });
    });
    return choices.sort((a, b) => a.order - b.order);
  }

  function suitVariants(program, component, color) {
    return program?.[component]?._pms.colors.get(color)?.variants || [];
  }

  function getSlackWaists(products) {
    const values = [];
    (products || []).forEach((product) => {
      const waistIndex = findOptionIndex(product, [/waist/i, /size/i]);
      (product.variants || []).forEach((variant) => {
        const value = variantOption(variant, waistIndex);
        if (value) values.push(value);
      });
    });
    return uniqueSorted(values);
  }

  function getSlackChoices(products, waist) {
    const choices = [];
    (products || []).forEach((product) => {
      const waistIndex = findOptionIndex(product, [/waist/i, /size/i]);
      const colorIndex = findOptionIndex(product, [/colou?r/i], waistIndex >= 0 ? [waistIndex] : []);
      (product.variants || []).forEach((variant) => {
        const row = normalizedVariant(product, variant);
        if (waist && variantOption(row, waistIndex) !== waist) return;
        const color = colorIndex >= 0 ? variantOption(row, colorIndex) : row.title;
        choices.push({
          id: row.id,
          variant: row,
          title: product.title,
          subtitle: color,
          detail: variantOption(row, waistIndex),
          image: row.image,
          available: canFulfillQuantity(row, 1),
        });
      });
    });
    return choices;
  }

  function getStretchSizeFamilies(products) {
    const values = [];
    (products || []).forEach((product) => {
      const sizeIndex = findOptionIndex(product, [/size/i]);
      (product.variants || []).forEach((variant) => {
        const size = canonicalSize(variantOption(variant, sizeIndex));
        if (size) values.push(size);
      });
    });
    const order = ['XS', 'S', 'M', 'L', 'XL', '2XL', '3XL', '4XL', '5XL'];
    return uniqueSorted(values, (a, b) => {
      const ai = order.indexOf(a); const bi = order.indexOf(b);
      if (ai >= 0 || bi >= 0) return (ai < 0 ? 99 : ai) - (bi < 0 ? 99 : bi);
      return String(a).localeCompare(String(b), undefined, { numeric: true });
    });
  }

  function classifySleeve(value, productTitle) {
    const text = normalize(`${value} ${productTitle}`);
    if (/short/.test(text)) return 'Short sleeve';
    if (/tall/.test(text)) return 'Long sleeve tall';
    return 'Long sleeve';
  }

  function getStretchShirtChoices(products, sizeFamily, whiteOnly = true) {
    const choices = [];
    (products || []).forEach((product) => {
      const sizeIndex = findOptionIndex(product, [/size/i]);
      const colorIndex = findOptionIndex(product, [/colou?r/i]);
      const sleeveIndex = findOptionIndex(product, [/sleeve/i, /length/i]);
      const fitIndex = findOptionIndex(product, [/fit/i]);
      const tallIndex = findOptionIndex(product, [/tall.*standard/i, /standard.*tall/i]);

      (product.variants || []).forEach((variant) => {
        const row = normalizedVariant(product, variant);
        const sizeLabel = variantOption(row, sizeIndex);
        if (sizeFamily && canonicalSize(sizeLabel) !== sizeFamily) return;
        const color = colorIndex >= 0 ? variantOption(row, colorIndex) : 'White';
        if (whiteOnly && color && !/white/i.test(color)) return;
        const fit = fitIndex >= 0 ? variantOption(row, fitIndex) : '';
        const tall = tallIndex >= 0 ? variantOption(row, tallIndex) : '';
        const sleeveRaw = sleeveIndex >= 0 ? variantOption(row, sleeveIndex) : product.title;
        const sleeve = classifySleeve(`${sleeveRaw} ${tall}`, product.title);
        choices.push({
          id: row.id,
          variant: row,
          title: product.title,
          subtitle: [fit, sleeve].filter(Boolean).join(' · '),
          detail: [sizeLabel, color].filter(Boolean).join(' · '),
          sleeve,
          fit,
          sizeFamily: canonicalSize(sizeLabel),
          image: row.image,
          available: canFulfillQuantity(row, 1),
        });
      });
    });
    return choices;
  }


  function traditionalFitLabel(product) {
    const title = String(product?.title || '');
    if (/tailored|athletic/i.test(title)) return 'Tailored Fit';
    if (/classic|regular/i.test(title)) return 'Classic Fit';
    return title || 'Standard Fit';
  }

  function traditionalVariantRows(products, filters = {}) {
    const rows = [];
    (products || []).forEach((product) => {
      const fit = traditionalFitLabel(product);
      if (filters.fit && fit !== filters.fit) return;
      const neckIndex = findOptionIndex(product, [/neck/i, /collar/i]);
      const sleeveIndex = findOptionIndex(product, [/sleeve/i, /length/i]);
      const colorIndex = findOptionIndex(product, [/colou?r/i]);
      (product.variants || []).forEach((variant) => {
        const row = normalizedVariant(product, variant);
        const collar = neckIndex >= 0 ? variantOption(row, neckIndex) : '';
        const sleeve = sleeveIndex >= 0 ? variantOption(row, sleeveIndex) : row.title;
        const color = colorIndex >= 0 ? variantOption(row, colorIndex) : 'White';
        if (filters.whiteOnly !== false && color && !/white/i.test(color)) return;
        if (filters.collar && collar !== filters.collar) return;
        rows.push({ product, row, fit, collar, sleeve, color, isShort: /short/i.test(String(sleeve) + ' ' + product.title) });
      });
    });
    return rows;
  }

  function getTraditionalFits(products, whiteOnly = true) {
    return uniqueSorted(traditionalVariantRows(products, { whiteOnly }).map((item) => item.fit));
  }

  function getTraditionalCollars(products, fit, whiteOnly = true) {
    return uniqueSorted(traditionalVariantRows(products, { fit, whiteOnly }).map((item) => item.collar).filter(Boolean));
  }

  function getTraditionalSleeveLengths(products, fit, collar, whiteOnly = true) {
    return uniqueSorted(
      traditionalVariantRows(products, { fit, collar, whiteOnly })
        .filter((item) => !item.isShort)
        .map((item) => item.sleeve)
        .filter(Boolean)
    );
  }

  function getTraditionalShirtChoices(products, filters = {}) {
    return traditionalVariantRows(products, filters)
      .filter((item) => item.isShort || !filters.longSleeve || item.sleeve === filters.longSleeve)
      .map((item) => ({
        id: item.row.id,
        variant: item.row,
        title: item.product.title,
        subtitle: item.isShort ? 'Short sleeve' : 'Long sleeve',
        detail: [item.fit, item.collar, item.sleeve, item.color].filter(Boolean).join(' · '),
        sleeve: item.isShort ? 'Short sleeve' : 'Long sleeve',
        fit: item.fit,
        collar: item.collar,
        image: item.row.image,
        available: canFulfillQuantity(item.row, 1),
      }));
  }

  function getTieChoices(products) {
    const choices = [];
    (products || []).forEach((product) => {
      const names = optionNames(product);
      const styleIndex = names.findIndex((name) => /(style|pattern|colou?r)/i.test(name));
      (product.variants || []).forEach((variant) => {
        const row = normalizedVariant(product, variant);
        const label = styleIndex >= 0 ? variantOption(row, styleIndex) : row.title;
        choices.push({
          id: row.id,
          variant: row,
          title: label || product.title,
          subtitle: product.title,
          detail: '',
          image: row.image,
          available: canFulfillQuantity(row, 1),
          search: normalize(`${label} ${product.title} ${row.sku}`),
        });
      });
    });
    return choices;
  }

  function getSockSizes(products) {
    const values = [];
    (products || []).forEach((product) => {
      const sizeIndex = findOptionIndex(product, [/size/i]);
      (product.variants || []).forEach((variant) => {
        const value = variantOption(variant, sizeIndex);
        if (value) values.push(value);
      });
    });
    return uniqueSorted(values);
  }

  function getSockChoices(products, size) {
    const choices = [];
    (products || []).forEach((product) => {
      const sizeIndex = findOptionIndex(product, [/size/i]);
      const colorIndex = findOptionIndex(product, [/colou?r/i], sizeIndex >= 0 ? [sizeIndex] : []);
      (product.variants || []).forEach((variant) => {
        const row = normalizedVariant(product, variant);
        if (size && variantOption(row, sizeIndex) !== size) return;
        const color = colorIndex >= 0 ? variantOption(row, colorIndex) : row.title;
        choices.push({
          id: row.id,
          variant: row,
          title: color,
          subtitle: product.title,
          detail: variantOption(row, sizeIndex),
          image: row.image,
          available: canFulfillQuantity(row, 1),
        });
      });
    });
    return choices;
  }

  function getSimpleVariantChoices(products, labelPatterns) {
    const choices = [];
    (products || []).forEach((product) => {
      const labelIndex = findOptionIndex(product, labelPatterns || [/style/i, /strap/i, /buckle/i, /colou?r/i]);
      (product.variants || []).forEach((variant) => {
        const row = normalizedVariant(product, variant);
        const label = labelIndex >= 0 ? variantOption(row, labelIndex) : row.title;
        choices.push({
          id: row.id,
          variant: row,
          title: label || product.title,
          subtitle: product.title,
          image: row.image,
          available: canFulfillQuantity(row, 1),
        });
      });
    });
    return choices;
  }

  function selectedVariant(products, variantId) {
    return flattenProducts(products).map((row) => row.variant).find((variant) => String(variant.id) === String(variantId)) || null;
  }

  function allCatalogVariants(catalog) {
    return [
      ...(catalog?.suitPrograms || []).flatMap((program) => [...(program.coat?.variants || []), ...(program.pant?.variants || [])]),
      ...flattenProducts(catalog?.slacks).map((row) => row.variant),
      ...flattenProducts(catalog?.traditionalShirts).map((row) => row.variant),
      ...flattenProducts(catalog?.stretchShirts).map((row) => row.variant),
      ...flattenProducts(catalog?.ties).map((row) => row.variant),
      ...flattenProducts(catalog?.socks).map((row) => row.variant),
      ...flattenProducts(catalog?.buckles).map((row) => row.variant),
      ...flattenProducts(catalog?.straps).map((row) => row.variant),
      ...flattenProducts(catalog?.sewingKit ? [catalog.sewingKit] : []).map((row) => row.variant),
      ...flattenProducts(catalog?.careBooklet ? [catalog.careBooklet] : []).map((row) => row.variant),
    ];
  }

  function collectInventoryDemand({ packageDefinition, state, catalog }) {
    const demand = new Map();
    const add = (variant, quantity, component) => {
      if (!variant || Number(quantity) <= 0) return;
      const id = String(variant.id);
      const row = demand.get(id) || { variant, variantId: id, quantity: 0, components: [] };
      row.quantity += Number(quantity);
      if (component) row.components.push(component);
      demand.set(id, row);
    };
    const pkg = packageDefinition;

    (state?.suits || []).slice(0, pkg.suitCount).forEach((selection, index) => {
      const program = (catalog?.suitPrograms || []).find((item) => item.id === selection.programId);
      add(program?.coat?.variants?.find((variant) => String(variant.id) === String(selection.coatVariantId)), 1, `Suit ${index + 1} coat`);
      add(program?.pant?.variants?.find((variant) => String(variant.id) === String(selection.pantVariantId)), 2, `Suit ${index + 1} matching pants`);
    });

    const addAllocation = (products, allocation, component) => {
      Object.entries(allocation || {}).forEach(([variantId, quantity]) => {
        add(selectedVariant(products, variantId), quantity, component);
      });
    };
    addAllocation(catalog?.slacks, state?.slacks, 'Golf slacks');
    const shirtProducts = pkg.shirtFamily === 'traditional' ? catalog?.traditionalShirts : catalog?.stretchShirts;
    addAllocation(shirtProducts, state?.shirts, pkg.shirtFamily === 'traditional' ? 'Traditional shirts' : 'Stretch shirts');
    addAllocation(catalog?.ties, state?.ties, 'Ties');
    addAllocation(catalog?.socks, state?.socks, 'Drymax socks');

    (state?.belts || []).slice(0, pkg.belts).forEach((belt, index) => {
      add(selectedVariant(catalog?.buckles, belt?.buckleVariantId), 1, `Belt ${index + 1} buckle`);
      add(selectedVariant(catalog?.straps, belt?.strapVariantId), 1, `Belt ${index + 1} strap`);
    });

    add(catalog?.sewingKit?.variants?.[0], pkg.sewingKits, 'Sewing kit');
    add(catalog?.careBooklet?.variants?.[0], pkg.careBooklets, 'Clothing care booklet');

    return Array.from(demand.values()).map((row) => ({
      ...row,
      ...inventoryStatus(row.variant, row.quantity),
    }));
  }

  function inventoryReport(args) {
    const lines = collectInventoryDemand(args);
    return {
      lines,
      shortages: lines.filter((line) => line.toOrder > 0),
      blocked: lines.filter((line) => !line.allowed),
      availableNow: lines.reduce((sum, line) => sum + line.availableNow, 0),
      toOrder: lines.reduce((sum, line) => sum + line.toOrder, 0),
      requested: lines.reduce((sum, line) => sum + line.requested, 0),
    };
  }

  function validateState({ packageDefinition, state, catalog }) {
    const errors = [];
    const pkg = packageDefinition;

    for (let index = 0; index < pkg.suitCount; index += 1) {
      const suit = state.suits[index];
      if (!suit?.programId || !suit?.colorKey || !suit?.coatVariantId || !suit?.pantVariantId) {
        errors.push(`Complete Suit ${index + 1}.`);
        continue;
      }
      const program = catalog.suitPrograms.find((item) => item.id === suit.programId);
      const coat = program?.coat.variants.find((variant) => variant.id === suit.coatVariantId);
      const pant = program?.pant.variants.find((variant) => variant.id === suit.pantVariantId);
      if (!canFulfillQuantity(coat, 1)) errors.push(`Suit ${index + 1} coat is unavailable.`);
      if (!canFulfillQuantity(pant, 2)) errors.push(`Suit ${index + 1} needs two available matching pants.`);
    }

    if (!state.slackWaist) errors.push('Choose a slack waist size.');
    if (sumAllocation(state.slacks) !== pkg.slacks) errors.push(`Select exactly ${pkg.slacks} slacks.`);
    if (pkg.shirtFamily === 'traditional') {
      if (!state.traditionalFit) errors.push('Choose one fit for all ten Traditional shirts.');
      if (!state.traditionalCollar) errors.push('Choose one collar size for all ten Traditional shirts.');
      if (!state.traditionalSleeve) errors.push('Choose one long-sleeve length for the Traditional shirts.');
    } else if (!state.shirtSize) {
      errors.push('Choose a shirt size family.');
    }
    if (sumAllocation(state.shirts) !== pkg.shirts) errors.push(`Select exactly ${pkg.shirts} shirts.`);
    if (sumAllocation(state.ties) !== pkg.ties) errors.push(`Select exactly ${pkg.ties} ties.`);
    if (!state.sockSize) errors.push('Choose a sock size.');
    if (sumAllocation(state.socks) !== pkg.socks) errors.push(`Select exactly ${pkg.socks} pairs of socks.`);

    for (let index = 0; index < pkg.belts; index += 1) {
      const belt = state.belts[index];
      if (!belt?.buckleVariantId || !belt?.strapVariantId) errors.push(`Complete Belt ${index + 1}.`);
      if (belt?.sizing === 'cut' && !belt?.cutSize) errors.push(`Choose the cut size for Belt ${index + 1}.`);
      if (belt?.sizing === 'cut' && !belt?.acknowledged) errors.push(`Acknowledge the final-sale warning for Belt ${index + 1}.`);
    }

    inventoryReport({ packageDefinition, state, catalog }).blocked.forEach((line) => {
      const title = line.variant.productTitle || line.components[0] || line.variant.title || 'An item';
      const available = line.tracked ? line.onHand : 0;
      errors.push(`${title} requires ${line.requested}, but only ${available} can be purchased.`);
    });

    return { valid: errors.length === 0, errors };
  }

  function commonMetadata(pkg, packageId, state) {
    return {
      packageId,
      packageCode: pkg.code,
      packageTitle: pkg.title,
      targetPrice: pkg.targetPrice,
      mission: String(state?.customer?.mission || '').trim(),
    };
  }

  function addAllocatedItems(items, products, allocation, component, metadata) {
    Object.entries(allocation || {}).forEach(([variantId, quantity]) => {
      if (Number(quantity) <= 0) return;
      const variant = selectedVariant(products, variantId);
      if (!variant) return;
      items.push({ variantId, quantity: Number(quantity), component, metadata: { ...metadata } });
    });
  }

  function createPackageId() {
    return `PMS-MISSION-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  }

  function annotatePlanInventory(items, catalog) {
    const variants = new Map(allCatalogVariants(catalog).map((variant) => [String(variant.id), variant]));
    const remaining = new Map();

    items.forEach((item) => {
      const variant = variants.get(String(item.variantId));
      const status = inventoryStatus(variant, item.quantity);
      let availableNow = status.availableNow;
      let toOrder = status.toOrder;

      if (status.tracked) {
        const left = remaining.has(String(item.variantId))
          ? remaining.get(String(item.variantId))
          : Math.max(0, Number(variant.inventoryQuantity) || 0);
        availableNow = Math.min(Number(item.quantity) || 0, left);
        toOrder = Math.max(0, (Number(item.quantity) || 0) - availableNow);
        remaining.set(String(item.variantId), Math.max(0, left - availableNow));
      }

      item.metadata = {
        ...(item.metadata || {}),
        inventoryTracked: status.tracked,
        availableNow,
        toOrder,
        fulfillmentNote: toOrder > 0
          ? `${availableNow} available now; ${toOrder} to be ordered`
          : status.tracked ? `${availableNow} available now` : 'Available',
      };
    });
    return items;
  }

  function buildCartPlan({ packageDefinition, state, catalog, packageId }) {
    const validation = validateState({ packageDefinition, state, catalog });
    if (!validation.valid) {
      const error = new Error(validation.errors.join(' '));
      error.validationErrors = validation.errors;
      throw error;
    }

    const pkg = packageDefinition;
    const id = packageId || createPackageId();
    const meta = commonMetadata(pkg, id, state);
    const items = [];

    state.suits.slice(0, pkg.suitCount).forEach((selection, index) => {
      const program = catalog.suitPrograms.find((item) => item.id === selection.programId);
      const coat = program.coat.variants.find((variant) => variant.id === selection.coatVariantId);
      const pant = program.pant.variants.find((variant) => variant.id === selection.pantVariantId);
      const suitMeta = { ...meta, suitSlot: index + 1, suitProgram: program.title, suitColor: selection.colorLabel };
      items.push({ variantId: coat.id, quantity: 1, component: `Suit ${index + 1} Coat`, metadata: suitMeta });
      items.push({ variantId: pant.id, quantity: 2, component: `Suit ${index + 1} Matching Pants`, metadata: { ...suitMeta, twoPantSuit: true } });
    });

    addAllocatedItems(items, catalog.slacks, state.slacks, 'Golf Slacks', meta);
    addAllocatedItems(
      items,
      pkg.shirtFamily === 'traditional' ? catalog.traditionalShirts : catalog.stretchShirts,
      state.shirts,
      pkg.shirtFamily === 'traditional' ? 'Traditional Shirts' : 'Stretch Shirts',
      meta
    );
    addAllocatedItems(items, catalog.ties, state.ties, 'Ties', meta);
    addAllocatedItems(items, catalog.socks, state.socks, 'Drymax Socks', meta);

    state.belts.slice(0, pkg.belts).forEach((belt, index) => {
      const beltMeta = {
        ...meta,
        beltSlot: index + 1,
        beltSizing: belt.sizing === 'cut' ? `Cut to ${belt.cutSize}` : 'Leave uncut',
        finalSale: belt.sizing === 'cut' ? 'Acknowledged' : '',
      };
      items.push({ variantId: belt.buckleVariantId, quantity: 1, component: `Belt ${index + 1} Buckle`, metadata: beltMeta });
      items.push({ variantId: belt.strapVariantId, quantity: 1, component: `Belt ${index + 1} Strap`, metadata: beltMeta });
    });

    if (catalog.sewingKit?.variants?.[0]) {
      items.push({ variantId: String(catalog.sewingKit.variants[0].id), quantity: pkg.sewingKits, component: 'Sewing Kit', metadata: meta });
    }
    if (catalog.careBooklet?.variants?.[0]) {
      items.push({ variantId: String(catalog.careBooklet.variants[0].id), quantity: pkg.careBooklets, component: 'Clothing Care Booklet', metadata: meta });
    }

    annotatePlanInventory(items, catalog);
    return { items, metadata: meta, inventory: inventoryReport({ packageDefinition, state, catalog }) };
  }

  function calculateRetailTotal(plan, catalog) {
    const priceMap = new Map();
    allCatalogVariants(catalog).forEach((variant) => priceMap.set(String(variant.id), Number(variant.price) || 0));
    return (plan?.items || []).reduce((sum, item) => sum + (priceMap.get(String(item.variantId)) || 0) * Number(item.quantity || 0), 0);
  }

  global.PMSMissionaryCore = Object.freeze({
    VERSION,
    PACKAGE_DEFINITIONS,
    normalize,
    compact,
    canonicalSize,
    variantOption,
    optionNames,
    findOptionIndex,
    inventoryStatus,
    canFulfillQuantity,
    normalizedVariant,
    flattenProducts,
    sumAllocation,
    updateAllocation,
    uniqueSorted,
    colorKey,
    createSuitProgram,
    suitColorChoices,
    suitVariants,
    getSlackWaists,
    getSlackChoices,
    getStretchSizeFamilies,
    getStretchShirtChoices,
    getTraditionalFits,
    getTraditionalCollars,
    getTraditionalSleeveLengths,
    getTraditionalShirtChoices,
    getTieChoices,
    getSockSizes,
    getSockChoices,
    getSimpleVariantChoices,
    selectedVariant,
    collectInventoryDemand,
    inventoryReport,
    validateState,
    createPackageId,
    buildCartPlan,
    calculateRetailTotal,
  });
})(typeof window !== 'undefined' ? window : globalThis);
